import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Divider,
  Stack,
  Alert,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material';
import {
  Home,
  ListAlt,
  ArrowBack,
  Edit,
  PlayArrow,
  CheckCircle,
  Cancel,
  PhotoCamera,
  Refresh,
  Visibility,
  Download
} from '@mui/icons-material';
import { getTaskById, updateTaskStatus, addPhotos, getAuditsByTask } from '../services/api';
import { useSnackbar } from 'notistack';
import { useParams, useNavigate } from 'react-router-dom';
import { BASE_URL_FOR_FILE } from '../../../../../utils/BaseUrl';

export const TaskDetail = () => {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [task, setTask] = useState(null);
  const [files, setFiles] = useState([]);
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  const loadTaskData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [taskRes, auditsRes] = await Promise.all([
        getTaskById(id),
        getAuditsByTask(id, { page: 1, limit: 50 }).catch(err => {
          console.warn('Could not load audits:', err);
          return { data: { audits: [] } };
        })
      ]);

      // Handle task data
      if (taskRes.data && taskRes.data.task) {
        setTask(taskRes.data.task);
      } else if (taskRes.data) {
        setTask(taskRes.data);
      } else {
        setTask(taskRes);
      }

      // Handle audits data
      if (auditsRes.data) {
        setAudits(auditsRes.data.audits || auditsRes.data.data || auditsRes.data || []);
      } else {
        setAudits(auditsRes.audits || auditsRes || []);
      }

    } catch (err) {
      console.error('Load task error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load task details';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadTaskData();
    } else {
      setError('No task ID provided');
      setLoading(false);
    }
  }, [id]);

  const handleStatusUpdate = async () => {
    try {
      await updateTaskStatus(id, { status: selectedStatus });
      enqueueSnackbar(`Task status updated to ${selectedStatus.replace('_', ' ')}`, { variant: 'success' });
      setStatusDialogOpen(false);
      loadTaskData();
    } catch (err) {
      console.error('Status update error:', err);
      const errorMessage = err.response?.data?.message || 'Failed to update status';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleFileUpload = async (before = true) => {
    if (files.length === 0) {
      enqueueSnackbar('Please select files first', { variant: 'warning' });
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      files.forEach(file => formData.append('images', file));
      formData.append('before', before.toString());

      await addPhotos(id, formData);
      enqueueSnackbar(`${before ? 'Before' : 'After'} photos uploaded successfully`, { variant: 'success' });
      setFiles([]);
      // Clear file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
      loadTaskData();
    } catch (err) {
      console.error('Upload error:', err);
      const errorMessage = err.response?.data?.message || 'Upload failed';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleFilesChange = (e) => {
    setFiles(Array.from(e.target.files || []));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'warning';
      case 'cancelled': return 'error';
      case 'overdue': return 'error';
      case 'pending': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'normal': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const isOverdue = () => {
    return task && task.status !== 'completed' &&
      task.status !== 'cancelled' &&
      new Date(task.dueAt) < new Date();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'in_progress': return <PlayArrow />;
      case 'completed': return <CheckCircle />;
      case 'cancelled': return <Cancel />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography>Loading task details...</Typography>
        </Stack>
      </Box>
    );
  }

  if (error || !task) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="error" gutterBottom>
              {error || 'Task not found'}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate(-1)}
              sx={{ mt: 2 }}
            >
              Back to Task List
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          underline="hover"
          color="inherit"
          onClick={() => navigate('/hospital')}
          sx={{ cursor: 'pointer' }}
        >
          <Home sx={{ mr: 0.5 }} fontSize="inherit" />
          Hospital
        </Link>
        <Link
          underline="hover"
          color="inherit"
          onClick={() => navigate('/hospital/housekeeping-and-linen-management/housekeeping/task-list')}
          sx={{ cursor: 'pointer' }}
        >
          Housekeeping
        </Link>
        <Link
          underline="hover"
          color="inherit"
          onClick={() => navigate('/hospital/housekeeping-and-linen-management/housekeeping/task-list')}
          sx={{ cursor: 'pointer' }}
        >
          Task List
        </Link>
        <Typography color="text.primary">
          Task {task.taskNumber || `#${task._id?.slice(-6)}`}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Task {task.taskNumber || `#${task._id?.slice(-6)}`}
          </Typography>
          <Typography color="text.secondary">
            Created on {new Date(task.createdAt).toLocaleDateString()}
            {task.createdBy && ` by ${task.createdBy.first_name} ${task.createdBy.last_name}`}
          </Typography>
        </Box>

        <Stack direction="row" spacing={2}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadTaskData}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={() => navigate(`/hospital/housekeeping-and-linen-management/housekeeping/task-edit/${task._id}`)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            startIcon={getStatusIcon(task.status)}
            onClick={() => {
              setSelectedStatus(task.status);
              setStatusDialogOpen(true);
            }}
          >
            Update Status
          </Button>
        </Stack>
      </Box>

      {/* Overdue Alert */}
      {isOverdue() && (
        <Alert severity="error" sx={{ mb: 3 }}>
          This task is overdue! Due date was {new Date(task.dueAt).toLocaleDateString()}.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Main Content - Left Side */}
        <Grid item xs={12} md={8}>
          {/* Task Overview Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Task Overview
              </Typography>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Task Type
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {task.taskType ? task.taskType.replace('_', ' ') : 'Not specified'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Priority
                  </Typography>
                  <Chip
                    label={task.priority}
                    color={getPriorityColor(task.priority)}
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={task.status ? task.status.replace('_', ' ') : 'pending'}
                    color={getStatusColor(task.status)}
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  />
                  {isOverdue() && (
                    <Chip
                      label="Overdue"
                      color="error"
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Due Date
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {task.dueAt ? new Date(task.dueAt).toLocaleString() : 'Not set'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Room Information Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Room Information
              </Typography>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Room Number
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {task.room?.room_no || 'Not specified'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Location
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {task.room?.location || 'Not specified'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Ward
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {task.ward?.name || task.room?.ward?.name || 'Not specified'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Floor
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {task.floor?.name || task.room?.floor?.name || 'Not specified'}
                  </Typography>
                </Grid>

                {task.room?.beds && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Bed Status
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {task.room.occupied_beds || 0} / {task.room.bed_count || 0} occupied
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Checklist Information */}
          {task.checklist && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Checklist: {task.checklist.title}
                </Typography>
                {task.checklist.description && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {task.checklist.description}
                  </Typography>
                )}
                {task.checklist.items && task.checklist.items.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Checklist Items:
                    </Typography>
                    <Stack spacing={1}>
                      {task.checklist.items.map((item, index) => (
                        <Box key={item} sx={{ p: 1, borderRadius: 1, border: "1px solid #eee" }}>
                          <Typography variant="subtitle2">{item.label}</Typography>

                          <Typography variant="caption" color="text.secondary">
                            Type: {item.type}  •  Required: {item.required ? "Yes" : "No"}
                          </Typography>

                          {item.type === "select" && item.options?.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              Options: {item.options.join(", ")}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Photos Section */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Photos & Documentation
              </Typography>

              {/* Photo Upload */}
              <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Upload New Photos
                </Typography>
                <input
                  type="file"
                  multiple
                  onChange={handleFilesChange}
                  accept="image/*"
                  style={{ marginBottom: '16px', width: '100%' }}
                />
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<PhotoCamera />}
                    onClick={() => handleFileUpload(true)}
                    disabled={files.length === 0 || uploading}
                    size="small"
                  >
                    {uploading ? 'Uploading...' : 'Upload Before Photos'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PhotoCamera />}
                    onClick={() => handleFileUpload(false)}
                    disabled={files.length === 0 || uploading}
                    size="small"
                  >
                    {uploading ? 'Uploading...' : 'Upload After Photos'}
                  </Button>
                </Box>
                {files.length > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {files.length} file(s) selected for upload
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Photos Display */}
              <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
                <Tab label={`Before Photos (${task.before_photos?.length || 0})`} />
                <Tab label={`After Photos (${task.after_photos?.length || 0})`} />
              </Tabs>

              {activeTab === 0 && (
                <PhotoSection
                  title="Before Photos"
                  photos={task.before_photos || []}
                  emptyMessage="No before photos uploaded yet"
                />
              )}

              {activeTab === 1 && (
                <PhotoSection
                  title="After Photos"
                  photos={task.after_photos || []}
                  emptyMessage="No after photos uploaded yet"
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar - Right Side */}
        <Grid item xs={12} md={4}>
          {/* Assigned Staff Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Assigned Staff
              </Typography>
              {task.assignedTo ? (
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    {task.assignedTo.first_name} {task.assignedTo.last_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {task.assignedTo.role || 'Housekeeping Staff'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {task.assignedTo.email}
                  </Typography>
                  {task.assignedTo.phone && (
                    <Typography variant="body2" color="text.secondary">
                      {task.assignedTo.phone}
                    </Typography>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => {/* Add contact functionality */ }}
                  >
                    Contact
                  </Button>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Not assigned to any staff member
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Task Timeline Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Task Timeline
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body2">
                    {new Date(task.createdAt).toLocaleString()}
                  </Typography>
                </Box>
                {task.startedAt && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Started
                    </Typography>
                    <Typography variant="body2">
                      {new Date(task.startedAt).toLocaleString()}
                    </Typography>
                  </Box>
                )}
                {task.completedAt && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Completed
                    </Typography>
                    <Typography variant="body2">
                      {new Date(task.completedAt).toLocaleString()}
                    </Typography>
                  </Box>
                )}
                {task.duration && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Duration
                    </Typography>
                    <Typography variant="body2">
                      {task.duration} minutes
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body2">
                    {new Date(task.updatedAt).toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Quality Audits Card */}
          <AuditsCard audits={audits} />
        </Grid>
      </Grid>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Task Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={selectedStatus}
              label="Status"
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          {selectedStatus === 'completed' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Marking as completed will set the completion timestamp and calculate task duration.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleStatusUpdate} variant="contained">
            Update Status
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Helper Components
const PhotoSection = ({ title, photos, emptyMessage }) => (
  <Box>
    <Typography variant="subtitle1" gutterBottom>
      {title}
    </Typography>
    {photos.length > 0 ? (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
        {photos.map((photo, index) => (
          <Box key={index} sx={{ position: 'relative' }}>
            <img
              src={`${BASE_URL_FOR_FILE}${photo}`}
              alt={`${title.toLowerCase()} ${index + 1}`}
              style={{
                width: 120,
                height: 120,
                borderRadius: 8,
                objectFit: 'cover',
                border: '2px solid #e0e0e0',
                cursor: 'pointer'
              }}
              onClick={() => window.open(`${BASE_URL_FOR_FILE}${photo}`, '_blank')}
            />
            <Tooltip title="View Full Size">
              <IconButton
                size="small"
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  backgroundColor: 'rgba(255,255,255,0.8)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
                }}
                onClick={() => window.open(`${BASE_URL_FOR_FILE}${photo}`, '_blank')}
              >
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ))}
      </Box>
    ) : (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
        {emptyMessage}
      </Typography>
    )}
  </Box>
);

const AuditsCard = ({ audits }) => (
  <Card>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Quality Audits
        </Typography>
        <Chip
          label={`${audits.length} audit(s)`}
          size="small"
          variant="outlined"
        />
      </Box>

      {audits.length > 0 ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Inspector</TableCell>
              <TableCell align="center">Score</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {audits.slice(0, 5).map((audit) => (
              <TableRow key={audit._id} hover>
                <TableCell>
                  {new Date(audit.inspectedAt || audit.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  {audit.inspectedBy
                    ? `${audit.inspectedBy.first_name} ${audit.inspectedBy.last_name}`
                    : audit.inspector || 'Unknown'
                  }
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={audit.score ? `${audit.score}%` : 'N/A'}
                    size="small"
                    color={audit.score >= 90 ? 'success' : audit.score >= 70 ? 'warning' : 'error'}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
          No quality audits conducted for this task
        </Typography>
      )}

      {audits.length > 5 && (
        <Button
          fullWidth
          variant="text"
          size="small"
          sx={{ mt: 1 }}
          onClick={() => {/* Navigate to full audits page */ }}
        >
          View All {audits.length} Audits
        </Button>
      )}
    </CardContent>
  </Card>
);

export default TaskDetail;